<template>
  <div class="analysis-page page-container bg-light-gradient">
    <div class="container-padded py-4">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h1 class="mb-0 animate-fade-in display-heading">
          <span class="icon icon-primary icon-circle me-2">
            <i class="fas fa-chart-line"></i>
          </span> 
          Statistiken & Analysen
        </h1>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Wird geladen...</span>
        </div>
      </div>

      <div v-else class="row g-4">
        <!-- Übersichtskarten -->
        <div class="col-md-3">
          <Card animateIn="fade-in-up">
            <div class="stat-card">
              <div class="stat-icon bg-primary-soft">
                <i class="fas fa-building"></i>
              </div>
              <div class="stat-content">
                <h3 class="stat-value">
                  {{ totalCompanies.count }}
                  <small class="text-muted ms-2" :class="getGrowthClass(totalCompanies.growth)">
                    {{ totalCompanies.growth > 0 ? '+' : '' }}{{ totalCompanies.growth }}%
                  </small>
                </h3>
                <p class="stat-label mb-0">Firmen</p>
              </div>
            </div>
          </Card>
        </div>

        <div class="col-md-3">
          <Card animateIn="fade-in-up" :style="{ animationDelay: '0.1s' }">
            <div class="stat-card">
              <div class="stat-icon bg-success-soft">
                <i class="fas fa-clipboard-check"></i>
              </div>
              <div class="stat-content">
                <h3 class="stat-value">
                  {{ totalTests.count }}
                  <small class="text-muted ms-2" :class="getGrowthClass(totalTests.growth)">
                    {{ totalTests.growth > 0 ? '+' : '' }}{{ totalTests.growth }}%
                  </small>
                </h3>
                <p class="stat-label mb-0">Tests</p>
              </div>
            </div>
          </Card>
        </div>

        <div class="col-md-3">
          <Card animateIn="fade-in-up" :style="{ animationDelay: '0.2s' }">
            <div class="stat-card">
              <div class="stat-icon bg-info-soft">
                <i class="fas fa-users"></i>
              </div>
              <div class="stat-content">
                <h3 class="stat-value">
                  {{ totalApplicants.count }}
                  <small class="text-muted ms-2" :class="getGrowthClass(totalApplicants.growth)">
                    {{ totalApplicants.growth > 0 ? '+' : '' }}{{ totalApplicants.growth }}%
                  </small>
                </h3>
                <p class="stat-label mb-0">Bewerber</p>
              </div>
            </div>
          </Card>
        </div>

        <div class="col-md-3">
          <Card animateIn="fade-in-up" :style="{ animationDelay: '0.3s' }">
            <div class="stat-card">
              <div class="stat-icon bg-warning-soft">
                <i class="fas fa-chart-pie"></i>
              </div>
              <div class="stat-content">
                <h3 class="stat-value">{{ averageScore }}%</h3>
                <p class="stat-label mb-0">Ø Testergebnis</p>
              </div>
            </div>
          </Card>
        </div>

        <!-- Charts -->
        <div class="col-md-6">
          <Card animateIn="fade-in-up" :style="{ animationDelay: '0.4s' }">
            <template v-slot:header>
              <h4 class="mb-0">Tests pro Firma</h4>
            </template>
            <div class="chart-container">
              <Bar :data="testsPerCompanyChart.data" :options="testsPerCompanyChart.options" />
            </div>
          </Card>
        </div>

        <div class="col-md-6">
          <Card animateIn="fade-in-up" :style="{ animationDelay: '0.5s' }">
            <template v-slot:header>
              <h4 class="mb-0">Bewerber pro Monat</h4>
            </template>
            <div class="chart-container">
              <Line :data="applicantsPerMonthChart.data" :options="applicantsPerMonthChart.options" />
            </div>
          </Card>
        </div>

        <!-- Detaillierte Statistiken -->
        <div class="col-md-12">
          <Card animateIn="fade-in-up" :style="{ animationDelay: '0.6s' }">
            <template v-slot:header>
              <div class="d-flex justify-content-between align-items-center">
                <h4 class="mb-0">Firmenübersicht</h4>
                <div class="btn-group">
                  <button 
                    class="btn btn-outline-secondary btn-sm" 
                    :class="{ active: sortBy === 'tests' }"
                    @click="sortBy = 'tests'"
                  >
                    Nach Tests
                  </button>
                  <button 
                    class="btn btn-outline-secondary btn-sm" 
                    :class="{ active: sortBy === 'applicants' }"
                    @click="sortBy = 'applicants'"
                  >
                    Nach Bewerbern
                  </button>
                </div>
              </div>
            </template>
            
            <div class="table-responsive">
              <table class="table table-hover">
                <thead>
                  <tr>
                    <th>Firma</th>
                    <th class="text-center">Tests</th>
                    <th class="text-center">Bewerber</th>
                    <th class="text-center">Ø Score</th>
                    <th class="text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="company in sortedCompanies" :key="company.id">
                    <td>
                      <div class="d-flex align-items-center">
                        <CompanyLogo :company="company" size="sm" class="me-2" />
                        {{ company.name }}
                      </div>
                    </td>
                    <td class="text-center">{{ company.tests_count }}</td>
                    <td class="text-center">{{ company.applicants_count }}</td>
                    <td class="text-center">
                      <div class="progress" style="height: 6px;">
                        <div 
                          class="progress-bar" 
                          :style="{ width: company.average_score + '%' }"
                          :class="getScoreClass(company.average_score)"
                        ></div>
                      </div>
                      <small class="text-muted">{{ company.average_score }}%</small>
                    </td>
                    <td class="text-center">
                      <span class="badge" :class="getStatusClass(company.status)">
                        {{ company.status }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Card from '@/components/ui/Card.vue';
import CompanyLogo from '@/components/ui/CompanyLogo.vue';
import { Bar, Line as LineChart } from 'vue-chartjs';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  BarElement, 
  LineElement,
  PointElement,
  Title, 
  Tooltip, 
  Legend 
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

export default {
  name: 'AdminAnalysis',
  components: {
    Card,
    CompanyLogo,
    Bar,
    Line: LineChart
  },
  data() {
    return {
      loading: true,
      sortBy: 'tests',
      monthlyStats: null,
      refreshInterval: null
    };
  },
  computed: {
    ...mapGetters({
      analyticsData: 'analytics/getAnalyticsData',
      isLoading: 'analytics/isLoading',
      error: 'analytics/getError'
    }),
    totalCompanies() {
      return this.analyticsData?.totalCompanies || { count: 0, growth: 0 };
    },
    totalTests() {
      return this.analyticsData?.totalTests || { count: 0, growth: 0 };
    },
    totalApplicants() {
      return this.analyticsData?.totalApplicants || { count: 0, growth: 0 };
    },
    averageScore() {
      return this.analyticsData?.averageScore || 0;
    },
    sortedCompanies() {
      if (!this.analyticsData?.companies) return [];
      
      return [...this.analyticsData.companies].sort((a, b) => {
        if (this.sortBy === 'tests') {
          return b.tests_count - a.tests_count;
        }
        return b.applicants_count - a.applicants_count;
      });
    },
    testsPerCompanyChart() {
      const companies = this.analyticsData?.companies || [];
      const labels = companies.map(c => c.name);
      const data = companies.map(c => c.tests_count);

      return {
        data: {
          labels,
          datasets: [{
            label: 'Anzahl Tests',
            data,
            backgroundColor: 'rgba(52, 144, 220, 0.5)',
            borderColor: 'rgb(52, 144, 220)',
            borderWidth: 1
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                stepSize: 1
              }
            }
          }
        }
      };
    },
    applicantsPerMonthChart() {
      const monthlyData = this.analyticsData?.applicantsPerMonth || [];
      const labels = monthlyData.map(d => d.month);
      const data = monthlyData.map(d => d.count);

      return {
        data: {
          labels,
          datasets: [{
            label: 'Bewerber',
            data,
            fill: true,
            borderColor: 'rgb(52, 144, 220)',
            backgroundColor: 'rgba(52, 144, 220, 0.1)',
            tension: 0.4
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                stepSize: 1
              }
            }
          }
        }
      };
    }
  },
  methods: {
    ...mapActions({
      fetchAnalytics: 'analytics/fetchAnalytics'
    }),
    getScoreClass(score) {
      if (score >= 80) return 'bg-success';
      if (score >= 60) return 'bg-info';
      if (score >= 40) return 'bg-warning';
      return 'bg-danger';
    },
    getStatusClass(status) {
      switch (status) {
        case 'active': return 'bg-success';
        case 'inactive': return 'bg-warning';
        case 'dormant': return 'bg-danger';
        default: return 'bg-secondary';
      }
    },
    getGrowthClass(growth) {
      if (growth > 0) return 'text-success';
      if (growth < 0) return 'text-danger';
      return 'text-muted';
    },
    async loadData() {
      try {
        this.loading = true;
        await this.fetchAnalytics();
        await this.fetchMonthlyStats();
      } catch (error) {
        console.error('Fehler beim Laden der Analysedaten:', error);
      } finally {
        this.loading = false;
      }
    },
    async fetchMonthlyStats() {
      try {
        const response = await this.$http.get('/api/admin/analytics/monthly-stats');
        this.monthlyStats = response.data;
      } catch (error) {
        console.error('Fehler beim Laden der monatlichen Statistiken:', error);
      }
    },
    startAutoRefresh() {
      // Aktualisiere die Daten alle 5 Minuten
      this.refreshInterval = setInterval(() => {
        this.loadData();
      }, 5 * 60 * 1000);
    },
    stopAutoRefresh() {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval);
        this.refreshInterval = null;
      }
    }
  },
  created() {
    this.loadData();
    this.startAutoRefresh();
  },
  beforeDestroy() {
    this.stopAutoRefresh();
  }
};
</script>

<style lang="scss" scoped>
.analysis-page {
  .stat-card {
    display: flex;
    align-items: center;
    padding: 1rem;
    
    .stat-icon {
      width: 48px;
      height: 48px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 1rem;
      
      i {
        font-size: 1.5rem;
        color: var(--bs-primary);
      }
    }
    
    .stat-content {
      .stat-value {
        font-size: 1.5rem;
        font-weight: 600;
        margin: 0;
        line-height: 1.2;
      }
      
      .stat-label {
        color: var(--bs-gray-600);
        font-size: 0.875rem;
      }
    }
  }
  
  .chart-container {
    height: 300px;
    position: relative;
  }
  
  .bg-primary-soft {
    background-color: rgba(var(--bs-primary-rgb), 0.1);
  }
  
  .bg-success-soft {
    background-color: rgba(var(--bs-success-rgb), 0.1);
  }
  
  .bg-info-soft {
    background-color: rgba(var(--bs-info-rgb), 0.1);
  }
  
  .bg-warning-soft {
    background-color: rgba(var(--bs-warning-rgb), 0.1);
  }
  
  .progress {
    background-color: var(--bs-gray-200);
  }
}

.growth-indicator {
  font-size: 0.875rem;
  font-weight: 500;
  
  &.positive {
    color: var(--bs-success);
  }
  
  &.negative {
    color: var(--bs-danger);
  }
}

.last-update {
  font-size: 0.75rem;
  color: var(--bs-gray-600);
  text-align: right;
  margin-top: 1rem;
}
</style> 